import { cloneDeep, maxBy } from "lodash";

import {
  ConditionType,
  CreateShippingRateGroupDto,
  RateConditionType,
  RateGroupType,
  ShippingRateGroupFe,
  ShopifyCurrencyType,
  VariationDto,
} from "@intelligems/ig-types";
import { groupRounder } from "@intelligems/ig-utils";
import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";

import { getVariationFeFromDto } from "../../api/formatters/from-dto/experiences/variations";
import {
  initialNewVariationState,
  initialShippingRateGroupsState,
  initialSingleVariationState,
  initialVariationState,
} from "../../api/initials";
import {
  ShippingRateGroupReducer,
  ShippingRateGroupReducerAction,
} from "../../api/reducers/shippingRateGroupReducer";
import { orderFlatThresholdRate } from "../../features/experienceTests/pages/shippingExperience/components/RateBuilder/util";
import { VariationFe } from "../../types/schemas/variations";
import { sortByOrder } from "../../utils/sortings";
import { getRounded } from "../../utils/variations";
import { IVariationsState } from "../types/variations";

const sliceInitialVariationState: IVariationsState = {
  isSuccess: false,
  variations: [],
  storedVariations: [],
  campaignBeingEditedVariation: undefined,
};

const variationsSlice = rtk.createSlice({
  name: "variations",
  initialState: sliceInitialVariationState,
  reducers: {
    resetVariations: (draft) => {
      draft = sliceInitialVariationState;
      return draft;
    },
    initVariationsState: (
      draft,
      action: PayloadAction<{
        variations: VariationDto[] | undefined;
        isSoloCampaign?: boolean;
        offerId?: string;
      }>
    ) => {
      draft.isSuccess = true;
      if (action.payload.variations) {
        draft.variations = getVariationFeFromDto(action.payload.variations);

        draft.storedVariations = getVariationFeFromDto(
          action.payload.variations
        );
      } else {
        if (action.payload.isSoloCampaign) {
          draft.variations = [initialSingleVariationState(false)];
        } else {
          draft.variations = initialVariationState(false);
        }
      }
      return draft;
    },
    initCampaignCreateVariationsState: (
      draft,
      action: PayloadAction<{
        variation: VariationFe;
      }>
    ) => {
      draft.campaignBeingEditedVariation = action.payload.variation;

      return draft;
    },
    resetCampaignBeingEditedVariation: (draft) => {
      draft.campaignBeingEditedVariation = undefined;

      return draft;
    },
    setVariations: (
      draft,
      action: PayloadAction<{ variations: VariationFe[] }>
    ) => {
      if (draft.variations) {
        draft.variations = action.payload.variations;
      }
      return draft;
    },
    setCampaignBeingEditedVariation: (
      draft,
      action: PayloadAction<{ variation: VariationDto | undefined }>
    ) => {
      if (action.payload.variation) {
        draft.campaignBeingEditedVariation = getVariationFeFromDto([
          action.payload.variation,
        ])[0];
      }

      return draft;
    },
    variationKeyChange: (
      draft,
      action: PayloadAction<{ index: number; value: string; key: string }>
    ) => {
      draft.variations[action.payload.index][action.payload.key] =
        action.payload.value;
      return draft;
    },
    toggleNoExperienceId: (draft, action: PayloadAction<{ index: number }>) => {
      if (draft.variations[action.payload.index].testExperienceId) {
        draft.variations[action.payload.index].testExperienceId = undefined;
      }

      return draft;
    },
    setCampaignTestExperienceId: (
      draft,
      action: PayloadAction<{ id: string; variationIndex: number }>
    ) => {
      draft.variations[action.payload.variationIndex].testExperienceId =
        action.payload.id;

      return draft;
    },
    toggleTierType: (
      draft,
      action: PayloadAction<{ selectedTierType: RateConditionType }>
    ) => {
      draft.variations = draft.variations.map((group: VariationFe) => {
        return {
          ...group,
          shippingRateGroups: group.shippingRateGroups!.map((rateGroup) => {
            return {
              ...rateGroup,
              rates: rateGroup.rates.map((rate) => {
                return {
                  ...rate,
                  condition:
                    rateGroup.groupType !== "tieredByPriceOrWeight"
                      ? undefined
                      : {
                          ...rate.condition!,
                          conditionType: action.payload.selectedTierType,
                        },
                };
              }),
            };
          }),
        };
      });
      return draft;
    },
    dispatchShippingRateGroups: (
      draft,
      action: PayloadAction<{
        reducer: ShippingRateGroupReducerAction;
        variationIndex: number;
      }>
    ) => {
      if (!draft.isSuccess) return draft;
      draft.variations[action.payload.variationIndex] =
        ShippingRateGroupReducer(
          draft.variations[action.payload.variationIndex],
          action.payload.reducer
        )!;
      return draft;
    },
    initializeRateType: (
      draft,
      action: PayloadAction<{
        shippingRateGroupType: RateGroupType;
        storeCurrencyCode: ShopifyCurrencyType;
        variationId: string;
        tierType: RateConditionType;
        unitType: ConditionType;
      }>
    ) => {
      if (!draft.isSuccess) return draft;
      const variationIndex = draft.variations.findIndex(
        (testGroup) => testGroup.id === action.payload.variationId
      );
      draft.variations[variationIndex].shippingRateGroups = [
        initialShippingRateGroupsState(
          action.payload.shippingRateGroupType,
          action.payload.storeCurrencyCode,
          action.payload.tierType,
          action.payload.unitType,
          0,
          action.payload.variationId
        ),
      ];

      return draft;
    },
    addShippingRate: (
      draft,
      action: PayloadAction<{
        variationId: string;
        shippingRateGroupType: RateGroupType;
        storeCurrencyCode: ShopifyCurrencyType;
        tierType: RateConditionType;
        unitType: ConditionType;
      }>
    ) => {
      if (!draft.isSuccess) return draft;
      const variationIndex = draft.variations.findIndex(
        (testGroup) => testGroup.id === action.payload.variationId
      );
      draft.variations[variationIndex].shippingRateGroups!.push(
        initialShippingRateGroupsState(
          action.payload.shippingRateGroupType,
          action.payload.storeCurrencyCode,
          action.payload.tierType,
          action.payload.unitType,
          draft.variations[variationIndex].shippingRateGroups!.length,
          action.payload.variationId
        )
      );
      return draft;
    },
    removeShippingRate: (
      draft,
      action: PayloadAction<{
        variationId: string;
        shippingRateGroupIndex: number;
      }>
    ) => {
      if (!draft.isSuccess) return draft;
      const variationIndex = draft.variations.findIndex(
        (testGroup) => testGroup.id === action.payload.variationId
      );
      let tempShippingRateGroups = (
        draft.variations[variationIndex].shippingRateGroups || []
      ).filter(
        (_: ShippingRateGroupFe, idx: number) =>
          idx !== action.payload.shippingRateGroupIndex
      );

      tempShippingRateGroups = tempShippingRateGroups.map(
        (rateGroup, index) => ({
          ...rateGroup,
          position: index,
        })
      );

      draft.variations[variationIndex].shippingRateGroups =
        tempShippingRateGroups;
      return draft;
    },
    deleteVariationByIndex: (
      draft,
      action: PayloadAction<{ index: number; useWideApp: boolean }>
    ) => {
      const slideWidth = action.payload.useWideApp ? 2000 : 1000;

      if (!draft.isSuccess) return draft;

      let tempTestGroups = draft.variations.filter(
        (_: VariationFe, idx: number) => idx !== action.payload.index
      );

      tempTestGroups = groupRounder(
        draft.variations
          .filter((_: VariationFe, idx: number) => idx !== action.payload.index)
          .map((group: VariationFe, index: number) => ({
            ...group,
            order: index,
            percentage: 100 / tempTestGroups.length,
            rounded: getRounded(index, tempTestGroups.length),
            width: slideWidth / tempTestGroups.length,
          }))
      );

      draft.variations = tempTestGroups;
      return draft;
    },
    addVariation: (draft) => {
      if (!draft.isSuccess) return draft;

      const maxOrder = maxBy(draft.variations, "order")?.order || 0;
      draft.variations = draft.variations.map(
        (testGroup: VariationFe, testGroupIndex) => {
          return {
            ...testGroup,
            percentage: 100 / (draft.variations.length + 1),
            rounded: testGroupIndex === 0 ? "left" : "",
            width: 1000 / (draft.variations.length + 1),
          };
        }
      );

      draft.variations.push(
        initialNewVariationState(maxOrder, draft.variations.length)
      );

      draft.variations = groupRounder(draft.variations);

      return draft;
    },
    reorderVariations: (
      draft,
      action: PayloadAction<{ startIndex: number; endIndex: number }>
    ) => {
      if (!draft.isSuccess) return draft;
      const [removed] = draft.variations.splice(action.payload.startIndex, 1);
      draft.variations.splice(action.payload.endIndex, 0, removed);
      draft.variations = draft.variations.map((tg: VariationFe, index) => ({
        ...tg,
        order: index,
        rounded: getRounded(index, draft.variations.length),
      }));

      draft.variations = draft.variations.sort(sortByOrder);
      return draft;
    },
    variationSlide: (draft, action: PayloadAction<VariationFe[]>) => {
      if (!draft.isSuccess) return draft;

      draft.variations = groupRounder(action.payload);
    },
    ensureRateGroupOrder: (draft) => {
      if (!draft.isSuccess) return draft;

      draft.variations.forEach((variation) => {
        if (variation["shippingRateGroups"]) {
          cloneDeep(variation["shippingRateGroups"]).forEach((rateGroup) =>
            rateGroup.rates.sort(orderFlatThresholdRate)
          );
        }
      });
      return draft;
    },
  },
  selectors: {
    selectedVariationsState: (state) => state,
    selectedVariations: (state) => state.variations || [],
  },
});

export const {
  resetVariations,
  initVariationsState,
  setVariations,
  dispatchShippingRateGroups,
  initializeRateType,
  removeShippingRate,
  addVariation,
  addShippingRate,
  toggleTierType,
  deleteVariationByIndex,
  variationKeyChange,
  variationSlide,
  reorderVariations,
  ensureRateGroupOrder,
  toggleNoExperienceId,
  setCampaignTestExperienceId,
  setCampaignBeingEditedVariation,
  initCampaignCreateVariationsState,
  resetCampaignBeingEditedVariation,
} = variationsSlice.actions;

export const variationsReducer = variationsSlice.reducer;
export const { selectedVariationsState, selectedVariations } =
  variationsSlice.selectors;
